import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Lock, Check, Loader2, ExternalLink, Archive, ArchiveRestore } from "lucide-react";
import { safeJsonRequest } from "../../utils/safeFetch";
import SSNCell from "./SSNCell";
import NotesPanel from "./NotesPanel";

export const STATUS_OPTIONS = [
  { value: "submitted", label: "Submitted", color: "text-blue-400 bg-blue-900/40" },
  { value: "in_review", label: "In Review", color: "text-yellow-400 bg-yellow-900/40" },
  { value: "accepted", label: "Accepted", color: "text-green-400 bg-green-900/40" },
  { value: "closed", label: "Closed", color: "text-slate-400 bg-slate-700" },
];

export const getStatusStyle = (status) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status);
  return opt ? opt.color : "text-slate-400 bg-slate-700";
};

export const getStatusLabel = (status) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status);
  return opt ? opt.label : status || "Submitted";
};

/**
 * ReferralCard — Expandable card with SSN controls, status workflow, notes, and bulk-select checkbox.
 */
const ReferralCard = ({ referral, token, onStatusChange, selected, onSelectToggle, onArchive }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(referral.status || "submitted");
  const [notesCount, setNotesCount] = useState((referral.notes || []).length);
  const r = referral;

  const clientName = r.client_name_omhc || r.client_name_prp || "Unknown";
  const category = r.referral_category || "N/A";
  const submitted = r.submitted_at
    ? new Date(r.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "N/A";

  const urgencyColor = {
    Urgent: "text-red-400 bg-red-900/40",
    "Semi-Urgent": "text-orange-400 bg-orange-900/40",
    Routine: "text-green-400 bg-green-900/40",
  }[r.urgency_level] || "text-slate-400 bg-slate-700";

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) return;
    setStatusLoading(true);
    try {
      await safeJsonRequest(`/api/admin/referrals/${r.id}/status`, {
        method: "PATCH",
        body: { status: newStatus },
        token,
      });
      setCurrentStatus(newStatus);
      if (onStatusChange) onStatusChange(r.id, newStatus);
    } catch {
      // silent
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <div
      className={`bg-slate-800 border rounded-xl overflow-hidden transition-colors ${
        selected ? "border-blue-500 ring-1 ring-blue-500/30" : "border-slate-700"
      }`}
      data-testid={`referral-card-${r.id}`}
    >
      {/* Header Row */}
      <div className="flex items-center">
        {/* Checkbox */}
        <label
          className="flex items-center justify-center pl-4 pr-1 py-4 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onSelectToggle && onSelectToggle(r.id)}
            className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
            data-testid={`select-referral-${r.id}`}
          />
        </label>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 px-3 py-4 flex items-center justify-between hover:bg-slate-750 transition-colors text-left"
          data-testid={`referral-toggle-${r.id}`}
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-sm font-bold text-white truncate">{clientName}</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${urgencyColor}`}>
                  {r.urgency_level || "N/A"}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusStyle(currentStatus)}`}>
                  {getStatusLabel(currentStatus)}
                </span>
                {notesCount > 0 && (
                  <span className="text-xs text-slate-500">({notesCount} notes)</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span>{category}</span>
                <span>{submitted}</span>
                <span className={r.email_sent ? "text-green-400" : "text-slate-500"}>
                  {r.email_sent ? "Email sent" : "Email pending"}
                </span>
              </div>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
        </button>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-700 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-xs text-slate-400 space-y-2">
              <p><span className="text-slate-500">ID:</span> <span className="font-mono text-slate-300">{r.id}</span></p>
              <p><span className="text-slate-500">Source:</span> {r.referral_source || "N/A"}</p>
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/referral/${r.id}`); }}
                className="mt-1 inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 font-medium"
                data-testid={`view-detail-${r.id}`}
              >
                <ExternalLink className="w-3 h-3" /> View Full Details
              </button>
              {onArchive && (
                <button
                  onClick={(e) => { e.stopPropagation(); onArchive(r.id, currentStatus === "archived"); }}
                  className="mt-1 ml-3 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 font-medium"
                  data-testid={`archive-${r.id}`}
                >
                  {currentStatus === "archived" ? <><ArchiveRestore className="w-3 h-3" /> Unarchive</> : <><Archive className="w-3 h-3" /> Archive</>}
                </button>
              )}
            </div>

            {/* Status Workflow */}
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Status</label>
              <div className="flex items-center gap-1.5 flex-wrap">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={(e) => { e.stopPropagation(); handleStatusChange(opt.value); }}
                    disabled={statusLoading}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      currentStatus === opt.value
                        ? `${opt.color} border-current ring-1 ring-current/30`
                        : "bg-slate-700 text-slate-400 border-slate-600 hover:border-slate-500 hover:text-slate-300"
                    }`}
                    data-testid={`status-${opt.value}-${r.id}`}
                  >
                    {currentStatus === opt.value && <Check className="w-3 h-3 inline mr-1" />}
                    {statusLoading && currentStatus !== opt.value ? "" : opt.label}
                    {statusLoading && <Loader2 className="w-3 h-3 inline ml-1 animate-spin" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* SSN Section */}
          <div className="mt-4 p-3 bg-slate-900/60 rounded-xl border border-slate-600">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> Protected Health Information — SSN
            </h4>
            <div className="space-y-2">
              {(r.referral_category === "OMHC" || r.referral_category === "Both") && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">OMHC SSN:</span>
                  <SSNCell
                    referralId={r.id}
                    ssnField="omhc"
                    maskedSsn={r.ssn_omhc_masked}
                    hasSsn={r.has_ssn_omhc}
                    token={token}
                    onAuditEvent={() => {}}
                  />
                </div>
              )}
              {(r.referral_category === "PRP" || r.referral_category === "Both") && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">PRP SSN:</span>
                  <SSNCell
                    referralId={r.id}
                    ssnField="prp"
                    maskedSsn={r.ssn_prp_masked}
                    hasSsn={r.has_ssn_prp}
                    token={token}
                    onAuditEvent={() => {}}
                  />
                </div>
              )}
              {r.referral_category !== "OMHC" && r.referral_category !== "PRP" && r.referral_category !== "Both" && (
                <p className="text-xs text-slate-500 italic">No SSN fields for this referral category</p>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <NotesPanel referralId={r.id} notes={r.notes || []} token={token} onNoteAdded={() => setNotesCount((c) => c + 1)} />
        </div>
      )}
    </div>
  );
};

export default ReferralCard;
